import { Button, Form, Modal, Popconfirm, Space, Table, Tag } from 'antd';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { httpClient } from '../../api/httpClient';
import { MYCInput, MYCSelect } from '../../components/input_form.component';
import { Endpoints } from '../../constants/endpoint.constant';
import { DefaultOrigins } from '../../constants/origins.constant';
import { useGet, usePost } from '../../hooks/useRest.hook';
import { useForm } from 'antd/es/form/Form';

export const RegionalManagersScreen = () => {
    const [form] = useForm();
    const [editForm] = useForm();
    const [editingManager, setEditingManager] = useState(null);
    const [deletingManagerId, setDeletingManagerId] = useState(null);
    const [addingDriverId, setAddingDriverId] = useState(null);
    const createRq = usePost(Endpoints.CREATE_REGIONAL_MANAGER);
    const updateRq = usePost(editingManager?.id ? Endpoints.UPDATE_REGIONAL_MANAGER(editingManager.id) : Endpoints.CREATE_REGIONAL_MANAGER);
    const managersRq = useGet(Endpoints.REGIONAL_MANAGERS);
    const originsRq = useGet(Endpoints.GET_ORIGINS);
    const origins = (Array.isArray(originsRq?.data) && originsRq.data.length > 0) ? originsRq.data : DefaultOrigins;

    const originOptions = origins?.map?.(origin => ({ value: origin.id, label: origin.name })) || [];
    const statusOptions = [
        { value: 1, label: 'Active' },
        { value: 0, label: 'Inactive' },
    ];

    const onFinish = async values => {
        try {
            await createRq.post(values);
            toast.success('Regional manager created');
            form.resetFields();
            managersRq.load();
        } catch (e) {
            console.error('[CREATE REGIONAL MANAGER] error', e);
        }
    };

    const columns = [
        {
            title: 'Name',
            key: 'name',
            render: (_, record) => `${record?.name || ''} ${record?.surname || ''}`.trim() || '-',
        },
        {
            title: 'Email',
            dataIndex: 'email',
        },
        {
            title: 'Number',
            dataIndex: 'number',
            render: value => value || '-',
        },
        {
            title: 'Province / Region',
            key: 'region',
            render: (_, record) => {
                const assignedOrigins = Array.isArray(record?.origins) && record.origins.length
                    ? record.origins
                    : (record?.originIds || [record?.originId]).filter(Boolean).map(originId => origins.find(origin => origin.id === originId) || { id: originId, name: originId });
                return assignedOrigins.map(origin => origin?.name || origin?.id).join(', ') || '-';
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: value => <Tag color={Number(value) === 1 ? 'green' : 'default'}>{Number(value) === 1 ? 'Active' : 'Inactive'}</Tag>,
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => {
                const isDriver = record?.driverId || (Array.isArray(record?.roles) && record.roles.includes('driver'));
                const managerId = record?.id || record?.userId;
                return <Space>
                <Button onClick={() => {
                    setEditingManager(record);
                    editForm.setFieldsValue({
                        name: record?.name || '',
                        surname: record?.surname || '',
                        email: record?.email || '',
                        number: record?.number || '',
                        status: Number(record?.status) === 0 ? 0 : 1,
                        originIds: (record?.originIds || [record?.originId]).filter(Boolean),
                        password: undefined,
                    });
                }}>Edit</Button>
                <Button
                    loading={addingDriverId === managerId}
                    disabled={!!isDriver}
                    onClick={() => onAddAsDriver(record)}
                >
                    {isDriver ? 'Driver Added' : 'Add as Driver'}
                </Button>
                <Popconfirm
                    title='Delete regional manager?'
                    description='This will remove their CMS access and cannot be undone.'
                    okText='Delete'
                    okButtonProps={{ danger: true, loading: deletingManagerId === record?.id }}
                    onConfirm={() => onDeleteManager(record)}
                >
                    <Button danger loading={deletingManagerId === record?.id}>Delete</Button>
                </Popconfirm>
            </Space>;
            },
        },
    ];

    const onEditFinish = async values => {
        try {
            const payload = { ...values };
            if (!payload.password) delete payload.password;
            await updateRq.post(payload);
            toast.success('Regional manager updated');
            setEditingManager(null);
            editForm.resetFields();
            managersRq.load();
        } catch (e) {
            console.error('[UPDATE REGIONAL MANAGER] error', e);
        }
    };

    const onDeleteManager = async manager => {
        const managerId = manager?.id || manager?.userId;
        if (!managerId) return toast.error('Regional manager id missing');

        setDeletingManagerId(managerId);
        try {
            const { data } = await httpClient.post(Endpoints.DELETE_REGIONAL_MANAGER(managerId), {});
            if (!data?.success) {
                const error = new Error(data?.message || 'Failed to delete regional manager');
                error.response = { data };
                throw error;
            }
            toast.success('Regional manager deleted');
            if (editingManager?.id === managerId) {
                setEditingManager(null);
                editForm.resetFields();
            }
            managersRq.load();
        } catch (e) {
            console.error('[DELETE REGIONAL MANAGER] error', e);
            toast.error(e?.response?.data?.message || 'Failed to delete regional manager');
        } finally {
            setDeletingManagerId(null);
        }
    };

    const onAddAsDriver = async manager => {
        const managerId = manager?.id || manager?.userId;
        if (!managerId) return toast.error('Regional manager id missing');

        setAddingDriverId(managerId);
        try {
            const { data } = await httpClient.post(Endpoints.ADD_REGIONAL_MANAGER_AS_DRIVER(managerId), {});
            if (!data?.success) {
                const error = new Error(data?.message || 'Failed to add regional manager as driver');
                error.response = { data };
                throw error;
            }
            toast.success('Regional manager added as driver');
            managersRq.load();
        } catch (e) {
            console.error('[ADD REGIONAL MANAGER AS DRIVER] error', e);
            toast.error(e?.response?.data?.message || 'Failed to add regional manager as driver');
        } finally {
            setAddingDriverId(null);
        }
    };

    return <>
        <span className='self-center text-xl my-5'>Regional Managers</span>
        <div className='mx-auto flex w-full max-w-6xl flex-col gap-8'>
            <div className='rounded-[24px] border border-[#ece5dd] bg-white p-6 shadow-[0_18px_55px_rgba(15,23,42,0.05)]'>
                <span className='mb-5 block text-lg font-semibold'>Create Regional Manager</span>
                <Form onFinish={onFinish} form={form} layout='vertical'>
                    <div className='grid grid-cols-1 gap-5 md:grid-cols-2'>
                        <MYCInput name='name' label='Name' required />
                        <MYCInput name='surname' label='Surname' />
                        <MYCInput name='email' label='Email' required />
                        <MYCInput name='password' label='Password' type='password' required />
                        <MYCInput name='number' label='Phone Number' />
                        <MYCSelect name='originIds' label='Province / Regions' options={originOptions} required multiple />
                        <div className='md:col-span-2 flex justify-end'>
                            <Button loading={createRq.loading} htmlType='submit' type='primary' className='px-10'>
                                Create Regional Manager
                            </Button>
                        </div>
                    </div>
                </Form>
            </div>

            <div className='rounded-[24px] border border-[#ece5dd] bg-white p-6 shadow-[0_18px_55px_rgba(15,23,42,0.05)]'>
                <span className='mb-5 block text-lg font-semibold'>Current Regional Managers</span>
                <Table
                    rowKey={record => record?.id || record?.userId || record?.email}
                    loading={managersRq.loading}
                    columns={columns}
                    dataSource={managersRq?.data?.list || []}
                    pagination={{ pageSize: 10 }}
                />
            </div>
        </div>
        <Modal
            open={!!editingManager}
            title='Edit Regional Manager'
            footer={null}
            onCancel={() => {
                setEditingManager(null);
                editForm.resetFields();
            }}
            destroyOnClose
        >
            <Form onFinish={onEditFinish} form={editForm} layout='vertical'>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    <MYCInput name='name' label='Name' required />
                    <MYCInput name='surname' label='Surname' />
                    <MYCInput name='email' label='Email' required />
                    <MYCInput name='number' label='Phone Number' />
                    <MYCSelect name='status' label='Status' options={statusOptions} required />
                    <MYCInput name='password' label='New Password' type='password' />
                    <div className='md:col-span-2'>
                        <MYCSelect name='originIds' label='Province / Regions' options={originOptions} required multiple />
                    </div>
                    <div className='md:col-span-2 flex justify-end gap-3'>
                        <Button onClick={() => {
                            setEditingManager(null);
                            editForm.resetFields();
                        }}>
                            Cancel
                        </Button>
                        <Button loading={updateRq.loading} htmlType='submit' type='primary'>
                            Save Changes
                        </Button>
                    </div>
                </div>
            </Form>
        </Modal>
    </>;
};
